"use client"

import React, { useState } from "react";
import moment from "moment";
import {
  Box,
  Grid,
  Typography,
  IconButton,
  Select,
  Option,
  Modal,
  Button,
} from "@mui/joy";
import { ArrowLeft, ArrowRight, CalendarMonth } from "@mui/icons-material";

// List of months
const months = moment.months();

// Generate days for the given month and year
const getDaysInMonth = (year: number, month: number): (number | null)[] => {
  const firstDay = moment(`${year}-${month + 1}-01`).day(); // Get first day of the month (0=Sunday, 6=Saturday)
  const daysInMonth = moment(`${year}-${month + 1}`).daysInMonth(); // Get total days
  return [...Array(firstDay).fill(null), ...Array.from({ length: daysInMonth }, (_, i) => i + 1)];
};

export default function Calender(){
  const [selectedDate, setSelectedDate] = useState(moment()); // Stores selected date
  const [month, setMonth] = useState(selectedDate.month()); // Current month (0-11)
  const [year, setYear] = useState(selectedDate.year()); // Current year
  const [open, setOpen] = useState(false); // Controls Date Picker modal

  const days = getDaysInMonth(year, month);

  // Handle date selection
  const handleDateClick = (day: number | null) => {
    if (day) setSelectedDate(moment(`${year}-${month + 1}-${day}`));
  };

  return (
    <Box sx={{ width: 320, textAlign: "center", p: 2, borderRadius: "md", bgcolor: "background.surface" }}>
      {/* Header: Month, Year & Navigation */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
        <IconButton size="sm" onClick={() => setMonth((prev) => (prev === 0 ? 11 : prev - 1))}>
          <ArrowLeft />
        </IconButton>
        <Select value={month} onChange={(e, newMonth) => setMonth(newMonth as number)}>
          {months.map((m, index) => (
            <Option key={index} value={index}>{m}</Option>
          ))}
        </Select>
        <Select value={year} onChange={(e, newYear) => setYear(newYear as number)}>
          {Array.from({ length: 10 }, (_, i) => year - 5 + i).map((y) => (
            <Option key={y} value={y}>{y}</Option>
          ))}
        </Select>
        <IconButton size="sm" onClick={() => setMonth((prev) => (prev === 11 ? 0 : prev + 1))}>
          <ArrowRight />
        </IconButton>
      </Box>

      {/* Days of the Week */}
      <Grid container columns={7} spacing={1} sx={{ mb: 1 }}>
        {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day) => (
          <Grid key={day} xs={1}>
            <Typography level="body-sm" sx={{ fontWeight: "bold" }}>{day}</Typography>
          </Grid>
        ))}
      </Grid>

      {/* Calendar Days */}
      <Grid container columns={7} spacing={1}>
        {days.map((day, index) => (
          <Grid key={index} xs={1}>
            <Box
              onClick={() => handleDateClick(day)}
              sx={{
                width: 32, height: 32, display: "flex", alignItems: "center", justifyContent: "center",
                borderRadius: "50%", cursor: day ? "pointer" : "default",
                bgcolor: day === selectedDate.date() && month === selectedDate.month() && year === selectedDate.year()
                  ? "primary.solidBg" : "transparent",
                color: day === selectedDate.date() && month === selectedDate.month() && year === selectedDate.year()
                  ? "white" : "text.primary",
                "&:hover": day ? { bgcolor: "neutral.softBg" } : {},
              }}
            >
              {day}
            </Box>
          </Grid>
        ))}
      </Grid>

      {/* Date Picker Button */}
      <Button onClick={() => setOpen(true)} startDecorator={<CalendarMonth />} sx={{ mt: 2 }}>
        Select Date
      </Button>

      {/* Date Picker Modal */}
      <Modal open={open} onClose={() => setOpen(false)}>
        <Box sx={{ width: 300, bgcolor: "background.surface", p: 2, borderRadius: "md" }}>
          <Typography level="title-lg">Pick a Date</Typography>
          <Typography level="body-sm">Selected: {selectedDate.format("MMMM DD, YYYY")}</Typography>
          <Calender />
          <Button onClick={() => setOpen(false)} sx={{ mt: 2 }}>Close</Button>
        </Box>
      </Modal>
    </Box>
  );
};
